import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PagedEntity, Style } from 'app/shared/model/app.model';
import { TagStyle, TagValueQuery, TagValue } from 'ng/src/app/tags/tag.model';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { LogService } from '../logs/log.service';
import { HALResponse } from '../shared/model/app.model';
import { GeneralParameters } from "app/shared/model/request-parameter.model";

@Injectable({
	providedIn: 'root'
})
export class TagService {
	constructor(
		private http: HttpClient
	) { }

	private tagStyles$?: Observable<TagStyle[]>;

	static getTagsAsCSVString(tagValues: TagValue[]) {
		return tagValues.map(tv => tv.stringValue)
			.sort((a, b) => a.localeCompare(b))
			.join(', ');
	}

	getTagStyles() {
		if (!this.tagStyles$) {
			this.tagStyles$ = this.http.get<HALResponse<{ tagStyles: TagStyle[] }>>(
				'api/tagStyles'
			)
			.pipe(
				map(r => r._embedded.tagStyles),
				shareReplay(1)
			)
		}

		return this.tagStyles$;
	}

	getTagValues(chunk: string) {
		return this.http.get<HALResponse<{ tagValues: TagValue[] }>>(
			`api/tagValues`, {
				params: {
					logId: LogService.selectedLog!.id.toString(),
					projection: 'tagValueProjectionWithParents',
					stringValue: chunk
				}
			}
		)
		.pipe(
			map(r => r._embedded.tagValues)
		);
	}
	
	fetchTagValues(query: TagValueQuery): Observable<PagedEntity<TagValue>> {
		const _query: GeneralParameters = { ...query };
		
		// Change parentDepthSpec to match the server requirements
		if (query.parentDepthSpec) {
			const parentDepthSpec = [query.parentDepthSpec.tagId];
			
			if (query.parentDepthSpec.depth) {
				parentDepthSpec.push(query.parentDepthSpec.depth);
			}
			
			_query.parentDepthSpec = parentDepthSpec;
		}
		
		return this.http.get<HALResponse<{ tagValues: TagValue[] }>>('/api/tagValues', {
			params: _query
		})
			.pipe(
				map(r => PagedEntity.from(r._embedded.tagValues, r.page))
			);
	}

	getTagValuesByLogId(logId: number) {
		return this.http.get<HALResponse<{ tagValues: TagValue[] }>>(
			`api/tagValues?log=${logId}&size=1000000`
		)
		.pipe(
			map(r => r._embedded.tagValues)
		);
	}

	getStylesForTagValues(tagValues: TagValue[]) {
		let priority = 0,
			styles: Style[] = [],
			selectedTag = '';

		for (let i = 0, tagValue; tagValue = tagValues[i]; i++) {
			let thisPriority = tagValue.priority || 0,
				hasStyles = tagValue.styles && tagValue.styles.length,
				isFirstWithStyles = styles == null && hasStyles,
				noPrioritiesSeen = priority == null && tagValue.priority == null;

			if (isFirstWithStyles 
				|| thisPriority > priority
				|| ((noPrioritiesSeen || thisPriority == priority) && hasStyles && tagValue.stringValue > selectedTag)) {
				
				priority = tagValue.priority || 0;
				styles = tagValue.styles;
				selectedTag = tagValue.stringValue;
			}
		}
		
		return styles.map(style => style.description);
	}
}